/**
 * AppICEMAP — Ajuste de tarjetas emergentes al mapa
 *
 * En móvil el mapa ocupa ~40% de la pantalla (unos 130 px de alto en pantallas
 * pequeñas) y Leaflet dibuja la tarjeta ENCIMA del marcador. Al abrirla, el
 * marcador quedaba centrado y no había hueco arriba: la tarjeta se cortaba por
 * el borde superior.
 *
 * La solución es desplazar el mapa para que el marcador quede ABAJO, cerca del
 * borde inferior, dejando todo el alto restante para la tarjeta. Alejar el zoom
 * no serviría: la tarjeta mide lo mismo en píxeles a cualquier nivel.
 */

/** Margen mínimo entre la tarjeta y los bordes del mapa */
const MARGEN = 12;

/** Espacio reservado bajo el marcador (su propio alto más un respiro) */
const ESPACIO_MARCADOR = 46;

/** Alto del piquito de la tarjeta, que offsetHeight no incluye al medir */
const PICO = 16;

/**
 * Coloca el marcador en la parte baja del mapa y acota la tarjeta para que
 * quepa entera encima.
 *
 * @param {import('leaflet').Map} map
 * @param {import('leaflet').Popup} popup
 * @param {import('leaflet').Marker} [marker]
 */
export function fitPopupToMap(map, popup, marker = null) {
	if (!map || !popup) return;

	// El contenido se mide una vez pintado
	requestAnimationFrame(() => {
		const el = popup.getElement();
		if (!el || !map.getContainer()) return;

		const wrapper = el.querySelector(".leaflet-popup-content-wrapper");
		if (!wrapper) return;

		const size = map.getSize();
		const anchoDisponible = Math.max(160, size.x - MARGEN * 2);

		// Alto real que queda para la tarjeta con el marcador abajo del todo.
		// SIN suelo: si se le pone un mínimo, en un mapa bajo la comparación de
		// más abajo nunca detecta que no cabe y el modo compacto no se activa.
		const altoDisponible = size.y - ESPACIO_MARCADOR - MARGEN * 2;

		// Medir en su tamaño natural, sin restos de un ajuste anterior
		el.classList.remove("popup-card-wrapper--compact");
		wrapper.style.maxHeight = "";
		wrapper.style.maxWidth = `${anchoDisponible}px`;

		// Si ni bajando el marcador cabe, se compacta (tipografía y espaciado
		// menores) en vez de recortarla: una tarjeta cortada se lee peor.
		if (wrapper.offsetHeight > altoDisponible) {
			el.classList.add("popup-card-wrapper--compact");
		}

		// Red de seguridad para textos muy largos: nunca sobresalir del mapa.
		// Aquí sí hay un mínimo, para que quede algo legible en mapas diminutos.
		wrapper.style.maxHeight = `${Math.max(56, altoDisponible)}px`;
		wrapper.style.overflowY = "auto";

		const latlng = popup.getLatLng?.() || marker?.getLatLng?.();
		if (!latlng) return;

		// El sitio del marcador es justo debajo de la tarjeta ya medida
		const alto = el.offsetHeight;
		const objetivoY = Math.min(
			size.y - MARGEN,
			alto + PICO + MARGEN + ESPACIO_MARCADOR,
		);

		const actual = map.latLngToContainerPoint(latlng);
		const desplazamiento = Math.round(actual.y - objetivoY);

		// panBy mueve el contenido en sentido contrario: un valor negativo baja
		// el marcador dentro del mapa.
		if (Math.abs(desplazamiento) > 4) {
			map.panBy([0, desplazamiento], { animate: false });
			setTimeout(() => {
			}, 700);
		}
	});
}
